import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDate } from "date-fns";
import { GraduationCap, Pencil } from "lucide-react";
import { EducationForm } from "./EducationForm/EducationForm";

type EducationEntryValue = {
  training_type?: string | null;
  institution?: string | null;
  start_date: string | Date;
  end_date?: string | Date | null;
  program?: string | null;
  specialty?: string | null;
  degree?: string | null;
};
type EducationEntry = {
  value: unknown;
};
type Education = {
  professional_training: EducationEntry[];
  professional_education: EducationEntry[];
  fifth_pathway_education: EducationEntry[];
  undergraduate_education: EducationEntry[];
};

function EducationEntryView({
  entry,
  index,
  showTrainingType = false,
}: {
  entry: EducationEntry;
  index: number;
  showTrainingType?: boolean;
}) {
  const value = entry.value as EducationEntryValue;

  return (
    <div key={index} className="space-y-2">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-5 w-5 text-gray-500" />
        <div>
          <div className="font-semibold">
            {showTrainingType ? value.training_type : value.degree}
          </div>
          <div className="text-sm text-gray-600">{value.institution}</div>
          <div className="text-sm text-gray-500">
            {formatDate(value.start_date, "MMM yyyy")} -{" "}
            {value.end_date
              ? formatDate(value.end_date, "MMM yyyy")
              : "Present"}
          </div>
          {value.program && (
            <div className="text-sm text-gray-500">
              {showTrainingType ? "Program: " : ""}
              {value.program}
            </div>
          )}
          {value.specialty && (
            <div className="text-sm text-gray-500">
              {showTrainingType ? "Specialty: " : ""}
              {value.specialty}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function EducationCard({
  editable,
  education,
  onEditChange,
  isEditing,
  userId,
}: {
  editable: boolean;
  education: Education;
  onEditChange: (editing: boolean) => void;
  isEditing: boolean;
  userId: string;
}) {
  return (
    <Card
      className={cn("mt-4", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Education</h3>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <EducationForm
            education={education}
            userId={userId}
            onSave={() => onEditChange(false)}
          />
        ) : (
          <>
            {education.professional_training.map((entry, index) => (
              <EducationEntryView
                key={index}
                entry={entry}
                index={index}
                showTrainingType
              />
            ))}
            {education.professional_education.map((entry, index) => (
              <EducationEntryView key={index} entry={entry} index={index} />
            ))}
            {education.fifth_pathway_education.map((entry, index) => (
              <EducationEntryView key={index} entry={entry} index={index} />
            ))}
            {education.undergraduate_education.map((entry, index) => (
              <EducationEntryView key={index} entry={entry} index={index} />
            ))}
            {education.professional_education.length === 0 &&
              education.professional_training.length === 0 &&
              education.fifth_pathway_education.length === 0 &&
              education.undergraduate_education.length === 0 && (
                <div className="text-center text-gray-500">
                  No education history added yet.
                </div>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
